// SPDX-FileCopyrightText: 2026 Povl Filip Sonne-Frederiksen
//
// SPDX-License-Identifier: GPL-3.0-or-later

import { useEffect, useId, useRef, useState, type ReactNode } from 'react';

import { useCanEdit } from '../../../app/CaseRoleContext';
import type { GeoWarning } from '../../../bygning/geometri';
import {
  MODIFY_GROUPS,
  TOOL_GROUPS,
  commandDef,
  commandTitle,
  toolHint,
  type GeometriCommand,
  type GeometriTool,
} from '../../../bygning/geometriKeys';
import { lengthInput, parseLength } from '../../../bygning/geometriEdit';
import { COMMAND_LABEL, type ModifySession } from '../../../bygning/modifyTool';
import type { XY } from '../../../bygning/planWalls';
import type { Placement, WallDrawOptions, WallMode } from '../../../bygning/wallDraw';
import { Kbd } from '../../Kbd';
import { CadIcon, commandIcon } from './cadIcons';
import styles from './GeometriChrome.module.css';

/** A hover / focus tip: the name and its key, as the dock and the bar show them. */
function Tip({ command, below = false }: { command: GeometriCommand; below?: boolean }) {
  const d = commandDef(command);
  return (
    <span className={below ? `${styles.tip} ${styles.tipBelow}` : styles.tip} role="presentation">
      {d.label}
      {d.keys && <Kbd>{d.keys}</Kbd>}
      {!d.ready && <span className={styles.soon}>kommer snart</span>}
    </span>
  );
}

// ------------------------------------------------------------ tool dock --

export interface ToolDockProps {
  tool: GeometriTool;
  onTool: (tool: GeometriTool) => void;
}

/**
 * The tool dock, bottom centre (Spacio): Vælg | Væg, Dæk/gulv, Rum, Zone |
 * Dør, Vindue, Åbning, Trappe | Mål, Målsætning. Icons with a tip naming the
 * tool and its key; a tool whose slice has not landed is shown dimmed and
 * says "kommer snart" — the toolbox is visible from the start, so the
 * editor's reach is legible, but nothing pretends to work.
 */
export function ToolDock({ tool, onTool }: ToolDockProps) {
  return (
    <div className={styles.dock} role="toolbar" aria-label="Værktøjer">
      {TOOL_GROUPS.map((group, gi) => (
        <div key={gi} className={styles.group}>
          {gi > 0 && <span className={styles.sep} aria-hidden="true" />}
          {group.map((t) => {
            const command = `tool:${t}` as const;
            const d = commandDef(command);
            const on = t === tool;
            return (
              <button
                key={t}
                type="button"
                className={`${styles.tool}${on ? ` ${styles.toolOn}` : ''}${d.ready ? '' : ` ${styles.toolSoon}`}`}
                aria-pressed={on}
                aria-disabled={!d.ready || undefined}
                aria-label={commandTitle(command)}
                aria-keyshortcuts={d.keys || undefined}
                onClick={() => onTool(t)}
              >
                <CadIcon name={commandIcon(command)} className={styles.toolIcon} />
                <Tip command={command} />
              </button>
            );
          })}
        </div>
      ))}
    </div>
  );
}

// --------------------------------------------------------- options strip --

const WALL_MODES: { value: WallMode; label: string; title: string }[] = [
  { value: 'line', label: 'Linje', title: 'Tegn en kæde af vægge, klik for hvert hjørne' },
  { value: 'rectangle', label: 'Rektangel', title: 'Fire vægge fra to modsatte hjørner' },
  { value: 'trace', label: 'Fra planspor', title: 'En væg på et scannet plans spor' },
];
const PLACEMENTS: { value: Placement; label: string; title: string }[] = [
  { value: 'center', label: 'Midte', title: 'Den tegnede linje er væggens midte' },
  { value: 'inside', label: 'Inde', title: 'Den tegnede linje er væggens indvendige flade' },
  { value: 'outside', label: 'Ude', title: 'Den tegnede linje er væggens udvendige flade' },
];

/** A length typed into the strip: set on Enter or leaving the field; empty = `empty` (when allowed). */
function LengthField({ label, value, placeholder, onChange, min }: { label: string; value: number | null; placeholder?: string; onChange: (v: number | null) => void; min: number }) {
  const [text, setText] = useState(value === null ? '' : lengthInput(value));
  useEffect(() => setText(value === null ? '' : lengthInput(value)), [value]);
  const commit = () => {
    if (text.trim() === '' && placeholder) return onChange(null);
    const v = parseLength(text);
    if (v !== null && v >= min) onChange(v);
    else setText(value === null ? '' : lengthInput(value));
  };
  return (
    <label className={styles.optField}>
      <span className={styles.optLabel}>{label}</span>
      <span className={styles.optInputWrap}>
        <input
          className={styles.optInput}
          value={text}
          placeholder={placeholder}
          inputMode="decimal"
          aria-label={label}
          onChange={(e) => setText(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              commit();
              e.currentTarget.blur();
            } else if (e.key === 'Escape') {
              e.stopPropagation();
              setText(value === null ? '' : lengthInput(value));
              e.currentTarget.blur();
            }
          }}
        />
        {text.trim() !== '' && <span className={styles.optUnit}>m</span>}
      </span>
    </label>
  );
}

function Segmented<T extends string>({ label, options, value, onChange }: { label: string; options: { value: T; label: string; title: string }[]; value: T; onChange: (v: T) => void }) {
  return (
    <div role="radiogroup" aria-label={label} className={styles.segs}>
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          role="radio"
          aria-checked={o.value === value}
          title={o.title}
          className={`${styles.seg} ${styles.segSm}${o.value === value ? ` ${styles.segOn}` : ''}`}
          onClick={() => onChange(o.value)}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

export interface WallOptionsStripProps {
  options: WallDrawOptions;
  onChange: (patch: Partial<WallDrawOptions>) => void;
}

/**
 * The Væg tool's options strip, right above the dock while the tool is on
 * (Revit's Options Bar, Spacio's sub-modes; mock 02): Linje | Rektangel |
 * Fra planspor, Tykkelse, Placering (where the drawn line sits in the
 * wall), Højde (empty = to the storey's ceiling) and Kæde.
 */
export function WallOptionsStrip({ options, onChange }: WallOptionsStripProps) {
  return (
    <div className={styles.strip} role="toolbar" aria-label="Væg: indstillinger">
      <span className={styles.stripTitle}>Væg</span>
      <Segmented label="Tegnemåde" options={WALL_MODES} value={options.mode} onChange={(mode) => onChange({ mode })} />
      <span className={styles.vsep} aria-hidden="true" />
      <LengthField label="Tykkelse" value={options.thickness} min={0.01} onChange={(v) => v !== null && onChange({ thickness: v })} />
      <div className={styles.optField}>
        <span className={styles.optLabel}>Placering</span>
        <Segmented label="Placering" options={PLACEMENTS} value={options.placement} onChange={(placement) => onChange({ placement })} />
      </div>
      <LengthField label="Højde" value={options.height} placeholder="Til loft" min={0.1} onChange={(height) => onChange({ height })} />
      {options.mode === 'line' && (
        <label className={styles.optCheck} title="Hvert klik fortsætter fra det sidste punkt">
          <input type="checkbox" checked={options.chain} onChange={(e) => onChange({ chain: e.target.checked })} />
          Kæde
        </label>
      )}
    </div>
  );
}

export interface ModifyStripProps {
  session: ModifySession;
  /** "Væg 4": what the command works against (the boundary, the wall to align). */
  subject: string | null;
  onCopy: (copy: boolean) => void;
  onDone: () => void;
}

/** A running modify command's strip: its name, what it works on, Kopi (Rotér / Spejl), Afslut. */
export function ModifyStrip({ session, subject, onCopy, onDone }: ModifyStripProps) {
  const c = session.command;
  const role = c === 'trim' ? 'Grænse' : c === 'align' ? 'Justerer' : c === 'join' ? 'Fra' : c === 'offset' ? 'Kopi af' : null;
  return (
    <div className={styles.strip} role="toolbar" aria-label={`${COMMAND_LABEL[c]}: indstillinger`}>
      <span className={styles.stripTitle}>{COMMAND_LABEL[c]}</span>
      {role && subject && (
        <span className={styles.optField}>
          <span className={styles.optLabel}>{role}</span>
          <b className={styles.optValue}>{subject}</b>
        </span>
      )}
      {(c === 'move' || c === 'copy' || c === 'rotate' || c === 'mirror') && (
        <span className={styles.optField}>
          <span className={styles.optLabel}>Valgt</span>
          <b className={styles.optValue}>{session.guids.length === 1 ? '1 element' : `${session.guids.length} elementer`}</b>
        </span>
      )}
      {(c === 'rotate' || c === 'mirror') && (
        <label className={styles.optCheck} title="Behold originalen og læg en kopi">
          <input type="checkbox" checked={session.copy} onChange={(e) => onCopy(e.target.checked)} />
          Kopi
        </label>
      )}
      <button type="button" className={styles.linkButton} onClick={onDone}>
        Afslut <Kbd>Esc</Kbd>
      </button>
    </div>
  );
}

// -------------------------------------------------------- selection bar --

export interface SelectionBarProps {
  /** "Væg 6", or "3 elementer". */
  title: string;
  count: number;
  /** Tilbage til detekteret applies to something selected. */
  revertable: boolean;
  /** Slet applies to something selected. */
  deletable: boolean;
  onCommand: (command: GeometriCommand) => void;
}

/**
 * The selection bar, top centre, dark (Spacio's contextual bar), only while
 * something is selected: what is selected, then Flyt, Kopiér, Rotér,
 * Spejl, Forskyd | Del, Saml hjørne, Forlæng/trim | Tilbage til
 * detekteret, Slet. The modify commands come with S4 and say so.
 */
export function SelectionBar({ title, count, revertable, deletable, onCommand }: SelectionBarProps) {
  const canEdit = useCanEdit();
  const button = (command: GeometriCommand, enabled: boolean) => {
    const d = commandDef(command);
    const ok = enabled && d.ready && canEdit;
    return (
      <button
        key={command}
        type="button"
        className={`${styles.barButton}${ok ? '' : ` ${styles.barSoon}`}${command === 'delete' ? ` ${styles.barDanger}` : ''}`}
        aria-disabled={!ok || undefined}
        aria-label={d.ready ? (d.keys ? `${d.label} (${d.keys})` : d.label) : commandTitle(command)}
        aria-keyshortcuts={d.keys || undefined}
        onClick={() => onCommand(command)}
      >
        <CadIcon name={commandIcon(command)} className={styles.barIcon} />
        <Tip command={command} below />
      </button>
    );
  };
  return (
    <div className={styles.bar} role="toolbar" aria-label={`Valgt: ${title}`}>
      <span className={styles.barTitle}>
        {title}
        <small>{count === 1 ? '1 valgt' : `${count} valgt`}</small>
      </span>
      {MODIFY_GROUPS.map((group, gi) => (
        <span key={gi} className={styles.barGroup}>
          <span className={styles.barSep} aria-hidden="true" />
          {group.map((c) => button(c, true))}
        </span>
      ))}
      <span className={styles.barGroup}>
        <span className={styles.barSep} aria-hidden="true" />
        {button('revert', revertable)}
        {button('delete', deletable)}
      </span>
    </div>
  );
}

// ---------------------------------------------------------- view cluster --

export type GeometriView = 'plan' | '3d';

export interface ViewClusterProps {
  view: GeometriView;
  onView: (view: GeometriView) => void;
  /** The scan cloud drawn under the model. */
  underlay: boolean;
  onUnderlay: () => void;
  onFit: () => void;
}

/** The view cluster, top right: Plan | 3D | Snit | Plan + snit, the scan underlay, zoom to fit. */
export function ViewCluster({ view, onView, underlay, onUnderlay, onFit }: ViewClusterProps) {
  const seg = (value: GeometriView | 'snit' | 'split', label: string, ready: boolean) => (
    <button
      key={value}
      type="button"
      role="radio"
      aria-checked={value === view}
      aria-disabled={!ready || undefined}
      className={`${styles.seg}${value === view ? ` ${styles.segOn}` : ''}${ready ? '' : ` ${styles.segSoon}`}`}
      title={ready ? undefined : `${label} kommer med snitværktøjet`}
      onClick={() => {
        if (ready && (value === 'plan' || value === '3d')) onView(value);
      }}
    >
      {label}
    </button>
  );
  return (
    <div className={styles.views}>
      <div role="radiogroup" aria-label="Visning" className={styles.segs}>
        {seg('plan', 'Plan', true)}
        {seg('3d', '3D', true)}
        {seg('snit', 'Snit', false)}
        {seg('split', 'Plan + snit', false)}
      </div>
      <span className={styles.vsep} aria-hidden="true" />
      <button
        type="button"
        className={`${styles.iconButton}${underlay ? ` ${styles.iconOn}` : ''}`}
        aria-pressed={underlay}
        title={underlay ? 'Skjul scanningen under modellen' : 'Vis scanningen under modellen'}
        aria-label="Scanning som underlag"
        onClick={onUnderlay}
      >
        <CadIcon name="cloud" className={styles.icon} />
      </button>
      <button type="button" className={styles.iconButton} title="Zoom til alt" aria-label="Zoom til alt" onClick={onFit}>
        <CadIcon name="fit" className={styles.icon} />
      </button>
    </div>
  );
}

// ---------------------------------------------------------- storey chip --

export interface StoreyChipProps {
  storeys: readonly { id: number; name: string; z: number }[];
  value: number | null;
  onChange: (id: number | null) => void;
}

const levelText = (z: number) => (Math.abs(z) < 0.005 ? '±0,00' : `${z > 0 ? '+' : '−'}${Math.abs(z).toFixed(2).replace('.', ',')}`);

/** The storey chip in the sub-tab bar ("Stueplan ±0,00 ▾"): which storey the plan cuts and the editor works on. */
export function StoreyChip({ storeys, value, onChange }: StoreyChipProps) {
  const current = storeys.find((s) => s.id === value) ?? null;
  return (
    <label className={styles.storey}>
      <span className={styles.storeyName}>{current ? current.name : 'Alle etager'}</span>
      {current && <small className={styles.storeyZ}>{levelText(current.z)}</small>}
      <CadIcon name="chevron" className={styles.storeyChevron} />
      <select
        className={styles.storeySelect}
        aria-label="Etage"
        value={value === null ? '' : String(value)}
        onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))}
      >
        <option value="">Alle etager</option>
        {storeys.map((s) => (
          <option key={s.id} value={s.id}>
            {s.name} {levelText(s.z)}
          </option>
        ))}
      </select>
    </label>
  );
}

// ---------------------------------------------------------- status line --

export interface StatusLineProps {
  tool: GeometriTool;
  selected: number;
  /** The Væg tool's sub-mode, for its hint. */
  wallMode?: 'line' | 'rectangle' | 'trace';
  /** A tool's own hint (S3: a drag's); else the tool's. */
  hint?: { lead: string; keys: { key: string; text: string }[] } | null;
  toggles: { key: string; label: string; on: boolean; title: string }[];
  onToggle: (key: string) => void;
  cursor: XY | null;
  /** A drag's readout replaces the cursor's ("Δx 5,70 Δy 0,00"). */
  readout?: string | null;
  warnings: readonly GeoWarning[];
  onPickWarning: (w: GeoWarning) => void;
  /** "Gemt · rev 142", "Gemmer…". */
  saved: string;
  canUndo: boolean;
  canRedo: boolean;
  onUndo: () => void;
  onRedo: () => void;
  /** The solids lag the model ("Dæk & tag forældet"). */
  stale: boolean;
  rederiving: boolean;
  onRederive: () => void;
}

const coord = (v: number) => v.toFixed(2).replace('.', ',').replace('-', '−');

/**
 * The status line, along the bottom (SketchUp's): the tool hint with its
 * keys, the snap toggles, the cursor (or a drag's readout), the warning
 * count (its popover replaces S1's always-on Advarsler panel), whether the
 * model is saved and at which revision, the solids' state, undo / redo.
 */
export function StatusLine(props: StatusLineProps) {
  const { tool, selected, toggles, onToggle, cursor, readout, warnings, onPickWarning, saved, canUndo, canRedo, onUndo, onRedo, stale, rederiving, onRederive } = props;
  const canEdit = useCanEdit();
  const hint = props.hint ?? toolHint(tool, selected, props.wallMode);
  const [open, setOpen] = useState(false);
  const popId = useId();
  const popRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const close = (e: PointerEvent) => {
      if (popRef.current && e.target instanceof Node && !popRef.current.contains(e.target)) setOpen(false);
    };
    const esc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        setOpen(false);
      }
    };
    window.addEventListener('pointerdown', close, true);
    window.addEventListener('keydown', esc, true);
    return () => {
      window.removeEventListener('pointerdown', close, true);
      window.removeEventListener('keydown', esc, true);
    };
  }, [open]);

  let solids: ReactNode = null;
  if (rederiving) solids = <span className={styles.statusNote}>Dæk & tag genberegnes…</span>;
  else if (stale)
    solids = (
      <button type="button" className={styles.linkButton} onClick={onRederive} disabled={!canEdit} title="Dæk og tag følger ikke de seneste ændringer — genberegn dem">
        Dæk & tag forældet · Genberegn
      </button>
    );

  return (
    <div className={styles.status} data-shell-footer="">
      <p className={styles.hint}>
        <b>{hint.lead}</b>
        {hint.keys.map((k) => (
          <span key={k.key} className={styles.hintKey}>
            <Kbd>{k.key}</Kbd>
            {k.text}
          </span>
        ))}
      </p>
      <div className={styles.right}>
        <div className={styles.toggles} role="group" aria-label="Fang">
          {toggles.map((t) => (
            <button key={t.key} type="button" className={`${styles.toggle}${t.on ? ` ${styles.toggleOn}` : ''}`} aria-pressed={t.on} title={t.title} onClick={() => onToggle(t.key)}>
              <i aria-hidden="true" />
              {t.label}
            </button>
          ))}
        </div>
        <span className={styles.readout} aria-live="off">
          {readout ?? (cursor ? `x ${coord(cursor[0])}  y ${coord(cursor[1])}` : '')}
        </span>
        {solids}
        <div className={styles.warnWrap} ref={popRef}>
          <button
            type="button"
            className={warnings.length > 0 ? `${styles.warnPill} ${styles.warnPillOn}` : styles.warnPill}
            aria-expanded={open}
            aria-controls={popId}
            title="Advarsler"
            onClick={() => setOpen((o) => !o)}
          >
            <CadIcon name="warning" className={styles.warnIcon} />
            {warnings.length}
          </button>
          {open && (
            <div id={popId} className={styles.popover} role="dialog" aria-label="Advarsler">
              <h2 className={styles.popTitle}>Advarsler ({warnings.length})</h2>
              {warnings.length === 0 ? (
                <p className={styles.popNone}>Ingen advarsler på det viste.</p>
              ) : (
                <ul className={styles.popList}>
                  {warnings.map((w) => (
                    <li key={w.id}>
                      <button
                        type="button"
                        className={styles.popItem}
                        onClick={() => {
                          onPickWarning(w);
                          setOpen(false);
                        }}
                      >
                        {w.text}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
        <span className={styles.saved}>{saved}</span>
        <div className={styles.undo}>
          <button type="button" className={styles.iconButtonSm} aria-label="Fortryd (⌘Z)" title="Fortryd (⌘Z)" disabled={!canUndo || !canEdit} onClick={onUndo}>
            <CadIcon name="undo" className={styles.iconSm} />
          </button>
          <button type="button" className={styles.iconButtonSm} aria-label="Gentag (⇧⌘Z)" title="Gentag (⇧⌘Z)" disabled={!canRedo || !canEdit} onClick={onRedo}>
            <CadIcon name="redo" className={styles.iconSm} />
          </button>
        </div>
      </div>
    </div>
  );
}
